import { useState } from "react";
import "./ClientsPage.css";
import { useAuth } from "../../../hooks/useAuth";
import { useEntitlements } from "../../../hooks/useEntitlements";
import { useClients } from "../../../hooks/useClients";
import {
  createClientAsync,
  deleteClientAsync,
  updateClientAsync,
} from "../../../api/ClientApi";
import {
  Client,
  CreateClientRequest,
  UpdateClientRequest,
} from "../../../models/ClientModels";
import { Constants } from "../../../constants/constants";
import { OrgForms } from "../../../config/forms";
import ClientModal, {
  ClientFormValues,
  ClientModalMode,
} from "./ClientModal";
import BulkImportModal from "./BulkImportModal";
import Toast from "../../../components/Toast";
import SubscriptionSectionGate from "../../../components/SubscriptionSectionGate";

interface IconProps {
  title?: string;
}

const EditIcon = ({ title }: IconProps) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden={title ? undefined : true}
  >
    {title && <title>{title}</title>}
    <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
  </svg>
);

const DeleteIcon = ({ title }: IconProps) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden={title ? undefined : true}
  >
    {title && <title>{title}</title>}
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);

const toUpdateRequest = (
  form: ClientFormValues,
): UpdateClientRequest => ({
  fullName: form.fullName || undefined,
  phone: form.phone || undefined,
  email: form.email || undefined,
  gender: form.gender || undefined,
  age: form.age ? Number(form.age) : undefined,
  address: form.address || undefined,
  city: form.city || undefined,
  area: form.area || undefined,
  notes: form.notes || undefined,
  isActive: form.isActive,
});

const ClientsPage = () => {
  const { user, hasPrivilege } = useAuth();
  const organizationId = user?.activeOrganization?.organizationId;
  const canCreateClients = hasPrivilege(OrgForms.Clients, "canCreate");
  const canEditClients = hasPrivilege(OrgForms.Clients, "canEdit");
  const canDeleteClients = hasPrivilege(OrgForms.Clients, "canDelete");
  const { entitlements, loading: entitlementsLoading } = useEntitlements();

  const {
    clients,
    pagination,
    loading,
    error,
    reload,
    filters,
    setFilters,
    setPage,
    addLocal,
    updateLocal,
    removeLocal,
  } = useClients(organizationId);

  const [searchInput, setSearchInput] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<ClientModalMode>("create");
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [bulkImportOpen, setBulkImportOpen] = useState(false);
  const [toast, setToast] = useState<{
    message: string;
    type: "success" | "error" | "info";
  } | null>(null);

  const downloadTemplate = () => {
    const headers = [
      "full_name",
      "phone",
      "email",
      "gender",
      "age",
      "address",
      "city",
      "area",
      "notes",
    ];
    const csv = headers.join(",") + "\n";
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "client_import_template.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  const openModal = (mode: ClientModalMode, client: Client | null) => {
    setModalMode(mode);
    setSelectedClient(client);
    setModalOpen(true);
  };

  const closeModal = () => setModalOpen(false);

  const handleSubmit = async (form: ClientFormValues) => {
    const token = localStorage.getItem(Constants.ACCESS_TOKEN) || "";
    setSubmitting(true);
    try {
      if (modalMode === "create") {
        if (!organizationId) {
          setToast({ message: "No active organization", type: "error" });
          return;
        }
        const payload: CreateClientRequest = {
          organizationId,
          fullName: form.fullName,
          phone: form.phone,
          email: form.email || undefined,
          gender: form.gender || undefined,
          age: form.age ? Number(form.age) : undefined,
          address: form.address || undefined,
          city: form.city || undefined,
          area: form.area || undefined,
          notes: form.notes || undefined,
          isActive: form.isActive,
        };
        const res = await createClientAsync(token, payload);
        if (res.success && res.result) {
          addLocal(res.result);
          closeModal();
          setToast({
            message: "Client added successfully!",
            type: "success",
          });
        } else {
          setToast({
            message: res.message || "Failed to add client",
            type: "error",
          });
        }
        return;
      }

      if (modalMode === "edit" && selectedClient) {
        const res = await updateClientAsync(
          token,
          selectedClient.id,
          toUpdateRequest(form),
        );
        if (res.success && res.result) {
          updateLocal(res.result);
          closeModal();
          setToast({
            message: "Client updated successfully!",
            type: "success",
          });
        } else {
          setToast({
            message: res.message || "Failed to update client",
            type: "error",
          });
        }
      }
    } catch (err) {
      console.error(err);
      setToast({
        message: "Something went wrong. Please try again.",
        type: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (client: Client) => {
    const token = localStorage.getItem(Constants.ACCESS_TOKEN) || "";
    setSubmitting(true);
    try {
      const res = await deleteClientAsync(token, client.id);
      if (res.success) {
        removeLocal(client.id);
        setToast({
          message: "Client deleted successfully!",
          type: "success",
        });
      } else {
        setToast({
          message: res.message || "Failed to delete client",
          type: "error",
        });
      }
    } catch (err) {
      console.error(err);
      setToast({
        message: "Failed to delete client. Please try again.",
        type: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const renderBody = () => {
    if (loading) {
      return <div className="clients-empty">Loading clients...</div>;
    }

    if (error) {
      return (
        <div className="clients-empty">
          <p>{error}</p>
          <button
            className="clients-btn clients-btn--secondary"
            onClick={reload}
          >
            Retry
          </button>
        </div>
      );
    }

    if (clients.length === 0) {
      return (
        <div className="clients-empty">
          {filters.search
            ? `No clients match "${filters.search}".`
            : "No clients found. Click \"Add Client\" to add one."}
        </div>
      );
    }

    return (
      <div className="clients-table-wrap">
        <table className="clients-table">
          <thead>
            <tr>
              <th>Client</th>
              <th>Phone</th>
              <th>Email</th>
              <th>Age</th>
              <th>City</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {clients.map((client) => (
              <tr key={client.id}>
                <td>
                  <div className="clients-name">
                    <span className="clients-avatar">
                      {client.fullName.charAt(0).toUpperCase()}
                    </span>
                    {client.fullName}
                  </div>
                </td>
                <td>{client.phone || "—"}</td>
                <td>{client.email || "—"}</td>
                <td>{client.age ?? "—"}</td>
                <td>{client.city || "—"}</td>
                <td>
                  <span
                    className={`clients-badge ${
                      client.isActive ? "active" : "inactive"
                    }`}
                  >
                    {client.isActive ? "Active" : "Inactive"}
                  </span>
                </td>
                <td className="clients-actions">
                  {canEditClients && (
                    <button
                      className="clients-icon-btn clients-icon-btn--edit"
                      title="Edit"
                      aria-label={`Edit client "${client.fullName}"`}
                      onClick={() => openModal("edit", client)}
                    >
                      <EditIcon />
                    </button>
                  )}
                  {canDeleteClients && (
                    <button
                      className="clients-icon-btn clients-icon-btn--delete"
                      title="Delete"
                      aria-label={`Delete client "${client.fullName}"`}
                      onClick={() => {
                        if (window.confirm(`Delete "${client.fullName}"?`)) {
                          handleDelete(client);
                        }
                      }}
                    >
                      <DeleteIcon />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  if (!entitlementsLoading && entitlements && !entitlements.hasActiveSubscription) {
    return (
      <div className="clients-page">
        <SubscriptionSectionGate section="clients" />
      </div>
    );
  }

  return (
    <div className="clients-page">
      <div className="clients-header">
        <div>
          <h2>Clients</h2>
          <p>
            Manage clients for{" "}
            {user?.activeOrganization?.name ?? "your organization"}
          </p>
        </div>
        {canCreateClients && (
          <div className="clients-header__actions">
            <button
              className="clients-btn clients-btn--secondary"
              onClick={downloadTemplate}
            >
              Download Template
            </button>
            <button
              className="clients-btn clients-btn--secondary"
              onClick={() => setBulkImportOpen(true)}
            >
              Import
            </button>
            <button
              className="clients-btn clients-btn--primary"
              onClick={() => openModal("create", null)}
            >
              + Add Client
            </button>
          </div>
        )}
      </div>

      <form
        className="clients-filters"
        role="search"
        onSubmit={(e) => {
          e.preventDefault();
          setFilters({ search: searchInput.trim() || undefined });
        }}
      >
        <label className="clients-filter clients-filter--grow">
          <span>Search</span>
          <input
            type="search"
            placeholder="Name or phone…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </label>
        <button type="submit" className="clients-btn clients-btn--secondary">
          Search
        </button>
        <button
          type="button"
          className="clients-btn clients-btn--secondary"
          onClick={() => {
            setSearchInput("");
            setFilters({ search: undefined });
          }}
        >
          Reset
        </button>
      </form>

      {renderBody()}

      {!loading &&
        !error &&
        pagination.totalCount > 0 && (
          <div className="clients-pagination">
            <span className="clients-pagination__info">
              Showing{" "}
              {(pagination.page - 1) * pagination.pageSize + 1}–
              {Math.min(
                pagination.page * pagination.pageSize,
                pagination.totalCount,
              )}{" "}
              of {pagination.totalCount}
            </span>
            <div className="clients-pagination__controls">
              <button
                className="clients-btn clients-btn--secondary"
                disabled={!pagination.hasPrevious}
                onClick={() => setPage(pagination.page - 1)}
              >
                Previous
              </button>
              <span className="clients-pagination__page">
                Page {pagination.page} of{" "}
                {Math.max(1, pagination.totalPages)}
              </span>
              <button
                className="clients-btn clients-btn--secondary"
                disabled={!pagination.hasNext}
                onClick={() => setPage(pagination.page + 1)}
              >
                Next
              </button>
            </div>
          </div>
        )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      <ClientModal
        isOpen={modalOpen}
        mode={modalMode}
        client={selectedClient}
        submitting={submitting}
        onClose={closeModal}
        onSubmit={handleSubmit}
      />

      <BulkImportModal
        isOpen={bulkImportOpen}
        organizationId={organizationId || ""}
        onClose={() => setBulkImportOpen(false)}
        onImported={() => {
          setBulkImportOpen(false);
          reload();
          setToast({ message: "Clients imported successfully!", type: "success" });
        }}
      />
    </div>
  );
};

export default ClientsPage;